import { getApiBaseUrl, requestJson } from "@/lib/api";

export type PricingSortColumn =
  | "changedAt"
  | "productName"
  | "tableName"
  | "actorEmail"
  | "oldPrice"
  | "newPrice";

export type SortDirection = "asc" | "desc";

export interface PricingChangeRow {
  productCode: number | null;
  productName: string;
  cutName: string | null;
  tableName: string;
  oldPrice: number;
  newPrice: number;
  /** Variação percentual ((novo-anterior)/anterior)×100; null quando anterior = 0. Calculada no backend. */
  changePercent: number | null;
  /** Data da alteração (DD/MM/AAAA, America/Sao_Paulo) — resolvida no backend. */
  date: string;
  /** Hora da alteração (HH:mm:ss, America/Sao_Paulo) — resolvida no backend. */
  time: string;
  changedAt: string;
  actorEmail: string;
  actorRole: string;
}

export interface PricingChangeReport {
  content: PricingChangeRow[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

export interface PricingChangeReportOptions {
  tables: string[];
  users: string[];
}

export interface PricingChangeReportParams {
  from?: string;
  to?: string;
  productName?: string;
  productCode?: number;
  cutName?: string;
  tableName?: string;
  actorEmail?: string;
  page?: number;
  size?: number;
  sort?: PricingSortColumn;
  direction?: SortDirection;
}

function toQuery(params: PricingChangeReportParams): string {
  const qs = new URLSearchParams();
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.productName) qs.set("productName", params.productName);
  if (params.productCode != null) qs.set("productCode", String(params.productCode));
  if (params.cutName) qs.set("cutName", params.cutName);
  if (params.tableName) qs.set("tableName", params.tableName);
  if (params.actorEmail) qs.set("actorEmail", params.actorEmail);
  if (params.page != null) qs.set("page", String(params.page));
  if (params.size != null) qs.set("size", String(params.size));
  if (params.sort) qs.set("sort", params.sort);
  if (params.direction) qs.set("direction", params.direction);
  return qs.toString();
}

export async function fetchPricingChangeReport(
  params: PricingChangeReportParams,
): Promise<PricingChangeReport> {
  const query = toQuery(params);
  return requestJson<PricingChangeReport>(`/api/v1/reports/pricing${query ? `?${query}` : ""}`);
}

export async function fetchPricingChangeReportOptions(): Promise<PricingChangeReportOptions> {
  return requestJson<PricingChangeReportOptions>("/api/v1/reports/pricing/options");
}

export async function downloadPricingChangeReportExport(
  params: PricingChangeReportParams,
): Promise<{ filename: string; blob: Blob }> {
  // A exportação ignora page/size (exporta todo o resultado filtrado), mas respeita filtros e ordenação.
  const { page: _page, size: _size, ...rest } = params;
  const query = toQuery(rest);
  const response = await fetch(
    `${getApiBaseUrl()}/api/v1/reports/pricing/export.xlsx${query ? `?${query}` : ""}`,
    { method: "GET", credentials: "include" },
  );
  if (!response.ok) {
    throw new Error("Não foi possível gerar o arquivo");
  }
  const disposition = response.headers.get("content-disposition") ?? "";
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  const filename = match?.[1] ?? "precificacao.xlsx";
  const blob = await response.blob();
  return { filename, blob };
}
